<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const router = useRouter()
const toast = useToastStore()
const incidentStore = useIncidentStore()
const authStore = useAuthStore()

const incidentName = ref('')
const incidentNumber = ref('')
const contactPhone = ref('')
const submitting = ref(false)

const canSubmit = computed(() => incidentName.value.trim() !== '' && !submitting.value)

async function handleSubmit() {
  if (!canSubmit.value) return
  const agencyId = authStore.userProfile?.agency_id ?? ''
  const userId = authStore.currentUser?.uid ?? ''

  submitting.value = true
  try {
    const { incidentId } = await incidentStore.createIncident({
      incident_name: incidentName.value.trim(),
      incident_number: incidentNumber.value.trim() || null,
      contact_phone: contactPhone.value.trim() || null,
      agency_id: agencyId,
      created_by: userId,
    })
    router.push({ name: 'incident-overview', params: { id: incidentId } })
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to create incident'
    toast.add(msg, 'error')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="bg-surface px-6 py-4 border-b border-outline-variant/15 shrink-0">
      <span class="text-on-surface text-lg font-semibold tracking-wide uppercase">First on Scene</span>
    </header>

    <div class="flex-1 overflow-y-auto">
    <div class="max-w-xl mx-auto px-4 py-10">
      <div class="bg-surface-container p-6 sm:p-10">
        <h1 class="text-xl font-bold text-on-surface uppercase tracking-wide mb-8">New Incident</h1>

        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-1.5">
            <label for="incident-name" class="text-[10px] font-label uppercase tracking-widest text-outline">
              Incident Name <span class="text-error">*</span>
            </label>
            <FosInput
              id="incident-name"
              v-model="incidentName"
              placeholder="e.g. Lost Hiker – Mt. Adams"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label for="incident-number" class="text-[10px] font-label uppercase tracking-widest text-outline">
              Incident Number <span class="text-outline font-normal">(optional)</span>
            </label>
            <FosInput
              id="incident-number"
              v-model="incidentNumber"
              placeholder="e.g. 2024-0042"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label for="contact-phone" class="text-[10px] font-label uppercase tracking-widest text-outline">
              Contact Phone <span class="text-outline font-normal">(optional — used on flyer)</span>
            </label>
            <FosInput
              id="contact-phone"
              v-model="contactPhone"
              type="tel"
              placeholder="e.g. (509) 555-0100"
            />
          </div>

          <FosButton
            variant="primary"
            class="w-full mt-2"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            <span v-if="submitting" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
            <span v-else>Start Incident</span>
          </FosButton>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>
